import { useState } from 'react'
import { FileUp, Loader2, X, FileText } from 'lucide-react'
import { uploadFile } from '@/lib/firestore-crud'

interface FileUploadProps {
  value?: string
  onChange: (url: string) => void
  folder: string
  accept?: string
}

export function FileUpload({ value, onChange, folder, accept = ".pdf" }: FileUploadProps) {
  const [uploading, setUploading] = useState(false)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    try {
      const fileName = `${Date.now()}-${file.name}`
      const url = await uploadFile(`${folder}/${fileName}`, file)
      onChange(url)
    } catch (error) {
      console.error('File upload failed:', error)
      alert('Upload file gagal. Pastikan Firebase Storage sudah dikonfigurasi.')
    } finally {
      setUploading(false)
    }
  }

  const clear = () => onChange('')

  const fileNameFromUrl = (url: string) => {
    try {
      const decoded = decodeURIComponent(url)
      return decoded.split('/').pop()?.split('?').shift() || 'file.pdf'
    } catch {
      return 'file.pdf'
    }
  }

  return (
    <div className="space-y-3">
      {value ? (
        <div className="flex items-center justify-between rounded-2xl border border-stone-800 bg-[#141414] p-4 text-sm text-stone-200">
          <div className="flex items-center gap-3 truncate">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-500/10 text-orange-500">
              <FileText className="h-5 w-5" />
            </div>
            <div className="truncate">
              <p className="font-medium truncate">{fileNameFromUrl(value)}</p>
              <a 
                href={value} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-orange-500 hover:underline"
              >
                Lihat File
              </a>
            </div>
          </div>
          <button
            onClick={clear}
            className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-stone-800 text-stone-400 transition hover:bg-stone-700 hover:text-white"
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <label className="flex h-32 w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-stone-800 bg-[#141414]/50 transition hover:border-orange-500/50 hover:bg-[#141414]">
          {uploading ? (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
              <span className="text-sm font-medium text-stone-500">
                Uploading...
              </span>
            </>
          ) : (
            <>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-stone-900 border border-stone-800">
                <FileUp className="h-5 w-5 text-stone-500" />
              </div>
              <div className="text-center px-4">
                <span className="text-sm font-semibold text-stone-200">
                  Klik untuk upload PDF manual
                </span>
                <p className="mt-1 text-xs text-stone-500">
                  Format: PDF (Max 10MB)
                </p>
              </div>
            </>
          )}
          <input
            type="file"
            className="hidden"
            accept={accept}
            onChange={handleFileChange}
            disabled={uploading}
          />
        </label>
      )}
    </div>
  )
}
